/* eslint-disable  react/jsx-boolean-value */
import React, { useRef, useState } from 'react';
import { Formik } from 'formik';
import { useSelector } from 'react-redux';
import { Spinner } from '@carvana/showroom-spinner';
import { useDualEventTracking } from '../../../../utils/useDualEventTracking';
import {
  NativeMessageEvent,
  sendNativeMessage,
} from '@carvana/core-native-messaging';
import html2canvas from 'html2canvas';
import { MyCarsService } from '../../../../services/index';
import { autopayEnrollmentFormValidationSchema } from './helpers/validationHelper';
import { getMyCars } from '../../../../ducks/selectors';
import EnrollmentSuccess from './enrollment-success/EnrollmentSuccess';
import EnrollmentFailure from './enrollment-failure/EnrollmentFailure';
import {
  AutopayEnrollmentWrapper,
  AutopayEnrollmentCheckbox,
  AutopayError,
  AutopayEnrollButton,
  AutopayEnrollWithCopyButton,
  AutopayConsentRow,
  AutopayButtonRow,
} from './AutopayEnrollment.styles';
import BridgecrestCustomError from '../../../future-packages/bridgecrest-custom-error';
import OopsError from '../../../future-packages/oops-error';

const AutopayEnrollment = (props: { vehicleId: any; autopayTermsRef: any }) => {
  const { vehicleId, autopayTermsRef } = props;
  const myCars = useSelector(getMyCars);
  const loanDetailCar: any = myCars.find(
    (car: any) => car.vehicle.vehicleId === Number(vehicleId)
  );
  const { autopayDetailsRequest, autopayDetailsPromptData } = loanDetailCar;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOneChecked, setIsOneChecked] = useState(false);
  const [isTwoChecked, setIsTwoChecked] = useState(false);
  const [isEnrollmentSuccess, setEnrollmentSuccess] = useState(false);
  const [isEnrollmentError, setEnrollmentError] = useState(false);
  const [isEnrollmentFailure, setEnrollmentFailure] = useState(false);
  const [isBridgecrestError, setBridgecrestError] = useState(false);
  const [bridgecrestErrorMessage, setBridgecrestErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [enrollAndSaveClicked, setEnrollAndSaveClicked] = useState(false);
  const { trackEvent } = useDualEventTracking();
  const checkBoxWrapper = useRef();

  const termsConsentClicked = () => {
    setIsOneChecked(!isOneChecked);
    trackEvent('confirm_auto_pay_terms_checkbox_tap');
  };

  const payConsentClicked = () => {
    setIsTwoChecked(!isTwoChecked);
    trackEvent('confirm_auto_pay_consent_checkbox_tap');
  };

  const handleEnrollmentSubmit = (result: any) => {
    setIsSubmitting(false);
    if (result.response) {
      result = result.response;

      if (
        result.data.httpStatusCode !== 'OK' &&
        result.data.errorCode === 'BridgecrestGraphQLErrorException'
      ) {
        setBridgecrestError(true);
        setBridgecrestErrorMessage(result.data.message);
      } else if (result.data.httpStatusCode !== 'OK') {
        setEnrollmentError(true);
      } else if (result.data.httpStatusCode === 'OK') {
        setEnrollmentFailure(true);
      }
    } else if (result.httpStatusCode === 'OK' && !result.hasError) {
      setEnrollmentSuccess(true);
    }
    setLoading(false);
  };

  const savePdf = async () => {
    const autopayTerms = autopayTermsRef?.current.cloneNode(true);
    const checkBoxes = checkBoxWrapper?.current?.cloneNode(true);
    if (autopayTerms) {
      autopayTerms.appendChild(checkBoxes);
      document.body.appendChild(autopayTerms);
      const canvas = await html2canvas(autopayTerms);
      const imgData = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = imgData;
      a.download = 'Bridgecrest-AutopayTerms.png';

      // Required for FireFox
      a.style.visibility = 'hidden';
      document.body.appendChild(a);

      // Initiate Download
      a.click();

      // Clean Up
      document.body.removeChild(a);
      document.body.removeChild(autopayTerms);
    } else {
      console.error('Error downloading Bridgecrest Autopay Terms');
    }
  };

  const closeBridgecrestCustomError = () => {
    setBridgecrestError(!isBridgecrestError);
    setBridgecrestErrorMessage('');
  };

  return (
    <>
      {loading && (
        <Spinner
          className="spinnerWrapper fullScreenSpinner"
          isFullScreen={true}
          opaqueBackground={true}
        />
      )}
      <AutopayEnrollmentWrapper>
        {isEnrollmentSuccess && (
          <EnrollmentSuccess
            isOpen={isEnrollmentSuccess}
            autopayDetailsPromptData={autopayDetailsPromptData}
          />
        )}
        {isEnrollmentError && (
          <OopsError
            isOpen={isEnrollmentError}
            closeModal={() => setEnrollmentError(false)}
          />
        )}
        {isEnrollmentFailure && (
          <EnrollmentFailure isOpen={isEnrollmentFailure} />
        )}
        {isBridgecrestError && (
          <BridgecrestCustomError
            isOpen={isBridgecrestError}
            header="AUTOPAY ENROLLMENT"
            title="UNABLE TO ENROLL IN AUTOPAY"
            message={bridgecrestErrorMessage}
            closeModal={closeBridgecrestCustomError}
          />
        )}
        <Formik
          initialValues={{
            enrollmentConsentOne: false,
            enrollmentConsentTwo: false,
          }}
          validationSchema={autopayEnrollmentFormValidationSchema}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={() => {
            setIsSubmitting(true);

            trackEvent('UE_enable_auto_pay', {
              vehicleId,
              amount: autopayDetailsRequest.withdrawAmount,
              startDate: autopayDetailsPromptData.amountDueDate,
            });

            MyCarsService.saveAutopayEnrollment(
              process.env.NODE_ENV,
              vehicleId,
              autopayDetailsRequest
            )
              .then((res) => {
                handleEnrollmentSubmit(res);
                return true;
              })
              .then((res) => {
                if (res && enrollAndSaveClicked) {
                  savePdf();
                }
              })
              .catch((err) => {
                setIsSubmitting(false);
                setLoading(false);
                console.error(err);
              });
          }}
          render={({ handleSubmit, handleChange, values, errors }) => {
            const isSubmitButtonDisabled =
              !values.enrollmentConsentOne ||
              !values.enrollmentConsentTwo ||
              isSubmitting;

            return (
              <form onSubmit={handleSubmit}>
                {errors.enrollmentConsentOne && (
                  <AutopayError>{errors.enrollmentConsentOne}</AutopayError>
                )}
                <div ref={checkBoxWrapper as any}>
                  <AutopayConsentRow>
                    <AutopayEnrollmentCheckbox
                      type="checkbox"
                      name="enrollmentConsentOne"
                      id="consentOne"
                      checked={isOneChecked}
                      error={errors.enrollmentConsentOne}
                      onChange={handleChange}
                      onClick={() => termsConsentClicked()}
                    />
                    <div>
                      By checking this box electronically, you agree to all
                      terms and conditions in this Voluntary Authorization for
                      Electronic (ACH) Payments as of the date shown above and
                      agree that you received a completed copy of this form.
                      Please print or keep a copy of this authorization with
                      your account records.
                    </div>
                  </AutopayConsentRow>

                  {errors.enrollmentConsentTwo && (
                    <AutopayError>{errors.enrollmentConsentTwo}</AutopayError>
                  )}
                  <AutopayConsentRow>
                    <AutopayEnrollmentCheckbox
                      type="checkbox"
                      name="enrollmentConsentTwo"
                      id="consentTwo"
                      checked={isTwoChecked}
                      error={errors.enrollmentConsentTwo}
                      onChange={handleChange}
                      onClick={() => payConsentClicked()}
                    />
                    <div>
                      I agree that Bridgecrest has the authority to initiate
                      automatic periodic electronic withdrawals from the bank
                      account I have identified and in the amounts and on the
                      Auto Pay payment schedule I have selected. I am
                      responsible for making any payments due before the start
                      date. I may cancel Auto Pay at any time via the Carvana
                      app, Carvana.com, or by following the instructions on
                      Bridgecrest.com.
                    </div>
                  </AutopayConsentRow>
                </div>
                <AutopayButtonRow>
                  <AutopayEnrollButton
                    disabled={isSubmitButtonDisabled}
                    type="submit"
                    id="autopayEnroll"
                    onClick={() => {
                      setLoading(true);
                      trackEvent('confirm_auto_pay_confirm_button_tap');
                      sendNativeMessage(
                        'confirm_auto_pay_save_copy_button_tap' as NativeMessageEvent,
                        {},
                        'mycars'
                      );
                    }}
                  >
                    ENROLL
                  </AutopayEnrollButton>
                  <AutopayEnrollWithCopyButton
                    disabled={isSubmitButtonDisabled}
                    type="submit"
                    id="autopayEnrollCopy"
                    onClick={() => {
                      setEnrollAndSaveClicked(true);
                      setLoading(true);
                      trackEvent('confirm_auto_pay_save_copy_button_tap');
                      sendNativeMessage(
                        'confirm_auto_pay_save_copy_button_tap' as NativeMessageEvent,
                        {},
                        'mycars'
                      );
                    }}
                  >
                    ENROLL AND SAVE A COPY
                  </AutopayEnrollWithCopyButton>
                </AutopayButtonRow>
              </form>
            );
          }}
        />
      </AutopayEnrollmentWrapper>
    </>
  );
};

export default AutopayEnrollment;
